// The agenda refreshes itself (D-225).
//
// Sending what waits has been automatic since the outbox engine; bringing back
// what changed was not. The agenda was read only when the day was prepared, so
// a preparation made on Friday still covered the following Monday and the day
// was drawn with Friday's slots: a slot another device had already closed went
// on reading "Por hacer", and the operator went to a site that was done. That
// is the case a tablet showed, and it is what this piece exists to close.
//
// Four rules, and nothing else:
//
//   * when the application opens with signal, if the stored agenda is not of
//     today — or is of another operator, or has gone past its freshness;
//   * every fifteen minutes while the application is open;
//   * when the signal comes back, whatever the freshness says: what changed
//     while the device was offline is exactly what it has not seen;
//   * never while a visit is being captured. A refresh replaces the seven days
//     whole, and the screen the operator is working on takes its slot from
//     them; the work waits until the visit is closed, and the day is not
//     something that changes under a hand that is writing into it.
//
// With no signal nothing is retried in a loop: the cycle ends at once and the
// timer and the return of the signal are what bring it back. A failure leaves
// the stored days exactly as they were, which is what the device shows, and
// the warning of a day prepared before today stays as the net underneath
// (D-221).
//
// B-73 (D-483): the forms that changed come back the same way. A change of
// template or an adjustment of a site reached the tablet only when somebody
// prepared the day, and nothing said it had not. Every cycle that gets past
// the session, the signal and the visit being captured reads the index of the
// forms and brings the ones of the sites the device has whose form changed
// (prep/forms.ts), whatever the agenda's freshness says: opening the
// application skips the agenda when it is fresh, never the forms. The two
// halves do not wait on each other's outcome: one failing leaves the other to
// do its part, and what the cycle answers is the agenda's.
import type { MeResponse } from '../api/types';
import type { FieldcoreDb } from '../db/database';
import { refreshForms } from '../prep/forms';
import type { SessionManager } from '../session/manager';
import { agendaIsStale, localDate, refreshAgenda } from './agenda';

/** How often the application looks again while it is open. */
export const AGENDA_REFRESH_INTERVAL_MS = 15 * 60 * 1000;

/** Why a cycle ran, and what it did of the agenda. Read by the tests and by
 *  nothing else; the forms' half answers nothing of its own (B-73). */
export type RefreshReason = 'app_open' | 'online' | 'periodic' | 'manual';

export type RefreshOutcome =
  | 'refreshed'
  /** Nothing to do: the stored agenda is of today and still fresh. */
  | 'fresh'
  | 'offline'
  | 'no_session'
  /** A visit is being captured: the seven days are left exactly as they are. */
  | 'capturing'
  | 'failed';

export interface AgendaRefresherDeps {
  db: FieldcoreDb;
  session: SessionManager;
  /** The instant the day is read at; the tests give their own. */
  now?: () => number;
  /** How often it looks again; the tests give a shorter one of their own. */
  intervalMs?: number;
}

/** Whether the signed-in operator has a visit open on this device.
 *
 *  A visit whose close the device already recorded is not being captured
 *  (D-226): it is closed, on every screen, and nothing more is written into
 *  it. Only `started` holds the agenda still. */
export async function isCapturing(db: FieldcoreDb, author: string | null): Promise<boolean> {
  const drafts = await db.visit_drafts.where('status').equals('started').toArray();
  return drafts.some((draft) => draft.author_id === author);
}

export class AgendaRefresher {
  private readonly db: FieldcoreDb;
  private readonly session: SessionManager;
  private readonly now: () => number;
  private readonly intervalMs: number;
  private timer: ReturnType<typeof setInterval> | null = null;
  private inflight: Promise<RefreshOutcome> | null = null;
  private unsubscribe: (() => void) | null = null;
  private signedIn = false;
  private readonly onOnline = (): void => {
    void this.refresh('online');
  };

  constructor(deps: AgendaRefresherDeps) {
    this.db = deps.db;
    this.session = deps.session;
    this.now = deps.now ?? ((): number => Date.now());
    this.intervalMs = deps.intervalMs ?? AGENDA_REFRESH_INTERVAL_MS;
  }

  /** Attach the triggers and look once, now. */
  start(): void {
    if (typeof window !== 'undefined') window.addEventListener('online', this.onOnline);
    this.timer ??= setInterval(() => {
      void this.refresh('periodic');
    }, this.intervalMs);
    // Signing in is the application opening for that person: the agenda it
    // finds on the device is whoever's was there before, and the day it draws
    // has to be this one's.
    this.unsubscribe ??= this.session.subscribe(() => {
      const authenticated = this.session.getSnapshot().status === 'authenticated';
      const arrived = authenticated && !this.signedIn;
      this.signedIn = authenticated;
      if (arrived) void this.refresh('app_open');
    });
    this.signedIn = this.session.getSnapshot().status === 'authenticated';
    void this.refresh('app_open');
  }

  stop(): void {
    if (typeof window !== 'undefined') window.removeEventListener('online', this.onOnline);
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  /** One cycle, or the one already running. Never rejects: failure is an outcome.
   *
   *  Opening the application is the one trigger that respects the freshness of
   *  what is stored: a reload two minutes after the last read has nothing to
   *  ask for. The quarter of an hour and the signal coming back exist to look
   *  again, so they read whatever the freshness says — what changed while the
   *  device was offline, or in the last quarter of an hour, is exactly what it
   *  has not seen.
   *
   *  B-73 (D-483): that freshness is the agenda's. The forms are read in every
   *  cycle, opening the application included, and a fresh agenda skips none. */
  async refresh(reason: RefreshReason): Promise<RefreshOutcome> {
    if (this.inflight !== null) return this.inflight;
    const force = reason !== 'app_open';
    this.inflight = this.cycle(force).finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  private async cycle(force: boolean): Promise<RefreshOutcome> {
    const snapshot = this.session.getSnapshot();
    const profile = snapshot.profile;
    if (snapshot.status !== 'authenticated' || profile === null) return 'no_session';
    if (!snapshot.online) return 'offline';
    // The rule that comes before every other: a visit being captured holds the
    // seven days still, whatever brought the cycle here.
    if (await isCapturing(this.db, profile.user.id)) return 'capturing';
    // B-73 (D-483): the forms, beside the agenda and whatever it answers.
    const [outcome] = await Promise.all([this.agenda(force, profile), this.forms()]);
    return outcome;
  }

  private async agenda(force: boolean, profile: MeResponse): Promise<RefreshOutcome> {
    const author = profile.user.id;
    const today = localDate(this.now(), profile.organization.timezone);
    if (!force && !(await agendaIsStale(this.db, { operatorId: author, today, now: this.now() }))) {
      return 'fresh';
    }
    try {
      await refreshAgenda(this.session.api, this.db, {
        operatorId: author,
        planningEnabled: profile.modules.includes('planning'),
        today,
      });
      return 'refreshed';
    } catch {
      // No signal halfway, or the server refused: the stored days stand, and
      // the next trigger — the timer, or the signal coming back — tries again.
      return 'failed';
    }
  }

  /** The forms of the sites the device has, brought when they changed. A
   *  failure leaves every stored form as it was, and the next trigger reads
   *  again; it never changes what the agenda's half answers. */
  private async forms(): Promise<void> {
    try {
      await refreshForms(this.session.api, this.db);
    } catch {
      // The stored forms stand: a visit opened now renders the one it had.
    }
  }
}
